import { isChunkLoadError, reloadForStaleBuild } from '@frontend/common';
import React, { Component, PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { Spinner } from './spinner.component';

interface ErrorBoundaryState {
    error: unknown;
    recovering: boolean;
}

/** @deprecated Use SentryErrorBoundary from @frontend/sentry instead. */
export class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
    override state: ErrorBoundaryState = { error: null, recovering: false };

    static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
        return { error };
    }

    override componentDidCatch(error: unknown) {
        if (isChunkLoadError(error) && reloadForStaleBuild()) {
            this.setState({ recovering: true });
        }
    }

    override render() {
        if (this.state.recovering) {
            return (
                <div className='d-flex flex-column justify-content-center align-items-center h-100 w-100'>
                    <Spinner />
                </div>
            );
        }

        if (this.state.error) {
            if (window.location.host.includes('terminal') || window.location.host.includes('localhost:8')) {
                setTimeout(() => window.location.reload(), 5000);
                return (
                    <div className='d-flex flex-column justify-content-center align-items-center h-100 w-100'>
                        <Spinner />
                    </div>
                );
            }

            return (
                <div className='d-flex flex-column justify-content-center align-items-center'>
                    <div className='card d-flex flex-column w-xl-50 w-100'>
                        <div className='card-header mb-0'>
                            <h1 className='text-primary'>Oops!</h1>
                        </div>
                        <div className='card-body mt-0'>
                            <p className='text-secondary'>
                                <FormattedMessage
                                    id='GlobalErrorBoundary.Explanation'
                                    description='Explanation for users that encountered a (uncaught) error.'
                                    defaultMessage='It seems like there is something wrong with the page you requested.
                                We are very sorry for the inconvenience. You can go back where you came from and try again. Or if this keeps happening, please contact us.'
                                />
                            </p>
                        </div>
                        <div className='card-footer'>
                            <button
                                className='btn btn-primary'
                                onClick={() => window.history.back()}>
                                Go back
                            </button>
                            <a
                                className='btn btn-secondary ms-2'
                                href={`mailto:support@telloport.com?subject=Platform%20Error&body=Please tell us about what you experienced here.%0A%0Ainfo:{href:${window.location.href}, path:${window.location.pathname}, ${this.state.error}}`}>
                                Contact support
                            </a>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
